import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, MapPin, Send, Check, AlertCircle, Search, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, Button, Input } from '@/components/ui';
import { submissionsApi, AutocompletePrediction } from '@/api/endpoints/submissions';

const faqItems = [
  {
    question: 'What is Bar Guide?',
    answer:
      'Bar Guide is a local bar specials discovery platform. We automatically find and display happy hour deals, food specials, and events from bars in your area.',
  },
  {
    question: 'How do you find specials?',
    answer:
      'We use AI to crawl bar websites and extract specials information. This runs automatically on a regular schedule to keep information fresh.',
  },
  {
    question: 'Is the information always accurate?',
    answer:
      "We do our best to keep information up-to-date, but specials can change. Always verify with the bar directly for the most current offers.",
  },
  {
    question: 'How can I claim my bar?',
    answer:
      'Bar owners can claim their establishment by visiting the bar page and clicking "Claim this bar." If your email matches the bar\'s website domain, you\'ll be automatically approved. Otherwise, an admin will review your request.',
  },
  {
    question: "How can I add a bar that's missing?",
    answer:
      "Sign in and use the submission form on this page! Search for the bar by name, select it from the results, and we'll add it to our database. The bar will be checked for specials within a few hours.",
  },
  {
    question: 'How do I delete my account?',
    answer:
      'Go to Settings, tap Account, then tap "Delete Account." This will permanently delete your account and all associated data including favorites, hidden bars, and submissions. This action cannot be undone.',
  },
];

function getStatusLabel(status: string, rejectionReason: string | null): { text: string; color: string } {
  switch (status) {
    case 'approved':
      return { text: 'Added', color: 'text-green-400' };
    case 'duplicate':
      return { text: 'Already exists', color: 'text-blue-400' };
    case 'rejected':
      const reasons: Record<string, string> = {
        place_not_found: 'Not found',
        non_us_location: 'Outside US',
        not_a_bar: 'Not a bar',
        no_website: 'No website',
      };
      return { text: reasons[rejectionReason || ''] || 'Rejected', color: 'text-red-400' };
    case 'pending':
      return { text: 'Pending', color: 'text-yellow-400' };
    default:
      return { text: status, color: 'text-[var(--color-muted)]' };
  }
}

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function HelpPage() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Autocomplete state
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompletePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<AutocompletePrediction | null>(null);

  // Success state
  const [submitSuccess, setSubmitSuccess] = useState<{
    name: string;
    id: string;
    alreadyExists: boolean;
  } | null>(null);

  // Generate session token once per form interaction
  const sessionToken = useMemo(() => crypto.randomUUID(), []);

  // Ref for debounce timeout
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch submission history (only when authenticated)
  const { data: historyData } = useQuery({
    queryKey: ['submission-history'],
    queryFn: () => submissionsApi.getHistory({ limit: 5, withinDays: 30 }),
    enabled: isAuthenticated,
  });

  // Debounced autocomplete search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchInput || searchInput.trim().length < 2 || selectedPlace) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await submissionsApi.getAutocomplete(searchInput, sessionToken);
        setSuggestions(response.predictions);
        setShowDropdown(response.predictions.length > 0);
      } catch (error) {
        console.error('Autocomplete error:', error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput, sessionToken, selectedPlace]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const submitMutation = useMutation({
    mutationFn: submissionsApi.submitBar,
    onSuccess: (data) => {
      setSearchInput('');
      setSelectedPlace(null);
      setSuggestions([]);
      setSubmitSuccess({
        name: data.bar.name,
        id: data.bar.id,
        alreadyExists: data.alreadyExists,
      });
      // Refresh submission history
      queryClient.invalidateQueries({ queryKey: ['submission-history'] });
    },
  });

  const handleSelectPlace = (place: AutocompletePrediction) => {
    setSelectedPlace(place);
    setSearchInput(place.name);
    setShowDropdown(false);
    setSuggestions([]);
    setSubmitSuccess(null);
  };

  const handleClearSelection = () => {
    setSelectedPlace(null);
    setSearchInput('');
    setSuggestions([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlace) return;
    setSubmitSuccess(null);
    submitMutation.mutate({
      placeId: selectedPlace.placeId,
      barName: selectedPlace.name,
    });
  };

  const getErrorMessage = (error: unknown): string => {
    // Check for Axios error response first (error.response.data.message)
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as { response: unknown }).response === 'object' &&
      (error as { response: { data?: unknown } }).response?.data &&
      typeof (error as { response: { data: { message?: unknown } } }).response.data.message === 'string'
    ) {
      return (error as { response: { data: { message: string } } }).response.data.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return 'Something went wrong. Please try again.';
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4">
      <div className="mx-auto max-w-2xl">
        {/* Back link */}
        <Link
          to="/"
          className="mb-6 flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Help</h1>
          <p className="mt-1 text-[var(--color-muted)]">
            Frequently asked questions{isAuthenticated ? ' and submit missing bars' : ''}
          </p>
        </div>

        {/* FAQ Section */}
        <div className={isAuthenticated ? 'mb-8' : ''}>
          <h2 className="mb-4 text-lg font-semibold">Frequently Asked Questions</h2>
          <div className="space-y-2">
            {faqItems.map((item, index) => (
              <Card key={index}>
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="flex w-full items-center justify-between p-4 text-left"
                >
                  <span className="font-medium pr-4">{item.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="h-5 w-5 shrink-0 text-[var(--color-muted)]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 shrink-0 text-[var(--color-muted)]" />
                  )}
                </button>
                {expandedFaq === index && (
                  <CardContent className="border-t border-[var(--color-border)] pt-4 pb-4 px-4">
                    <p className="text-[var(--color-muted)]">{item.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Submit Bar Section - Only show when authenticated */}
        {isAuthenticated && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-primary/10">
                  <MapPin className="h-5 w-5 text-accent-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Submit a Missing Bar</h2>
                  <p className="text-sm text-[var(--color-muted)]">
                    Know a great bar we're missing? Add it here!
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Search for a Bar</label>
                  <div className="relative" ref={dropdownRef}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" />
                      <Input
                        type="text"
                        placeholder="Type bar name..."
                        value={searchInput}
                        onChange={(e) => {
                          setSearchInput(e.target.value);
                          if (selectedPlace) {
                            setSelectedPlace(null);
                          }
                        }}
                        disabled={submitMutation.isPending}
                        className="pl-10 pr-10"
                      />
                      {(searchInput || selectedPlace) && (
                        <button
                          type="button"
                          onClick={handleClearSelection}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)] hover:text-[var(--color-text)]"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Dropdown */}
                    {showDropdown && suggestions.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-[var(--color-border)] bg-black shadow-lg">
                        {suggestions.map((place) => (
                          <button
                            key={place.placeId}
                            type="button"
                            onClick={() => handleSelectPlace(place)}
                            className="flex w-full flex-col items-start bg-black px-4 py-3 text-left transition-colors hover:bg-[var(--color-border)] first:rounded-t-lg last:rounded-b-lg"
                          >
                            <span className="font-medium text-[var(--color-text)]">{place.name}</span>
                            <span className="text-sm text-[var(--color-muted)]">
                              {place.address}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Loading indicator */}
                    {isSearching && (
                      <div className="absolute right-10 top-1/2 -translate-y-1/2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-muted)] border-t-transparent" />
                      </div>
                    )}
                  </div>

                  {selectedPlace ? (
                    <p className="mt-1.5 text-xs text-green-400">
                      Selected: {selectedPlace.name} - {selectedPlace.address}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-[var(--color-muted)]">
                      Search for a bar by name and select it from the results
                    </p>
                  )}
                </div>

                {submitMutation.error && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{getErrorMessage(submitMutation.error)}</span>
                  </div>
                )}

                {submitSuccess && (
                  <div className="flex items-start gap-2 rounded-lg bg-green-500/10 p-3 text-sm text-green-400">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {submitSuccess.alreadyExists ? (
                        <>
                          {submitSuccess.name} is already in our database!{' '}
                          <Link to={`/bars/${submitSuccess.id}`} className="underline">
                            View bar
                          </Link>
                        </>
                      ) : (
                        <>
                          {submitSuccess.name} has been added! Specials will be populated within a few hours.{' '}
                          <Link to={`/bars/${submitSuccess.id}`} className="underline">
                            View bar
                          </Link>
                        </>
                      )}
                    </span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={!selectedPlace || submitMutation.isPending}
                  className="w-full"
                >
                  {submitMutation.isPending ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Bar
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-[var(--color-muted)]">
                  Limited to 5 submissions per day. US bars only.
                </p>
              </form>

              {/* Recent Submissions */}
              {historyData && historyData.submissions.length > 0 && (
                <div className="mt-6 border-t border-[var(--color-border)] pt-6">
                  <h3 className="mb-3 text-sm font-medium">Your Recent Submissions</h3>
                  <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--color-border)]">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Bar</th>
                          <th className="px-3 py-2 text-center font-medium">Status</th>
                          <th className="px-3 py-2 text-right font-medium">When</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-border)]">
                        {historyData.submissions.map((submission) => {
                          const status = getStatusLabel(submission.status, submission.rejectionReason);
                          return (
                            <tr key={submission.id}>
                              <td className="px-3 py-2">
                                {submission.barId ? (
                                  <Link
                                    to={`/bars/${submission.barId}`}
                                    className="hover:text-accent-primary hover:underline"
                                  >
                                    {submission.barName || 'Unknown bar'}
                                  </Link>
                                ) : (
                                  <span className="text-[var(--color-muted)]">
                                    {submission.barName || 'Unknown bar'}
                                  </span>
                                )}
                              </td>
                              <td className={`px-3 py-2 text-center ${status.color}`}>
                                {status.text}
                              </td>
                              <td className="px-3 py-2 text-right text-[var(--color-muted)]">
                                {getRelativeTime(submission.createdAt)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
